import type {
  AppShellSidebarNavGroup,
  AppShellSidebarNavItem,
} from '@/components/layout/dashboard/app-shell-sidebar-nav.types';
import { CalendarClock, DatabaseZap, Flag, Logs, RefreshCw, Trash2 } from 'lucide-react';

export type AdminPanelSection =
  | 'caching'
  | 'ingestion'
  | 'cleanup'
  | 'analysed-data'
  | 'feature-flags'
  | 'export-logs';

export const ADMIN_PANEL_CURRENT_TENANT_GROUP_TITLE = 'Current tenant';
export const ADMIN_PANEL_DATA_MANAGEMENT_GROUP_TITLE = 'Data Management';
export const ADMIN_PANEL_REVENUE_GROUP_TITLE = 'Revenue';
export const ADMIN_PANEL_LOGS_GROUP_TITLE = 'Logs';

const ADMIN_PANEL_DATA_MANAGEMENT_ITEMS: AppShellSidebarNavItem<AdminPanelSection>[] = [
  {
    id: 'caching',
    title: 'Caching',
    icon: DatabaseZap,
    description: 'Invalidate client-side query caches or reset the server-side cache.',
  },
  {
    id: 'ingestion',
    title: 'Ingestion',
    icon: RefreshCw,
    description: 'Refresh ingestion pipelines for tenant data, selections, or specific interaction IDs.',
  },
  {
    id: 'cleanup',
    title: 'Cleanup',
    icon: Trash2,
    description: 'Remove analytics data or taxonomy rows from the warehouse for all projects or the current project.',
  },
  {
    id: 'analysed-data',
    title: 'Analysed Data',
    icon: CalendarClock,
    description:
      'Pin the last day of analysed data for the selected project. Relative time ranges resolve against this date as effective "today".',
  },
];

const ADMIN_PANEL_REVENUE_ITEMS: AppShellSidebarNavItem<AdminPanelSection>[] = [
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    icon: Flag,
    description: 'Enable or disable beta project features such as AI Fluency Index and ROI analysis.',
  },
];

const ADMIN_PANEL_LOGS_ITEMS: AppShellSidebarNavItem<AdminPanelSection>[] = [
  {
    id: 'export-logs',
    title: 'Download Logs',
    icon: Logs,
    description: 'Export application logs for a selected project and time range.',
  },
];

export const ADMIN_PANEL_SIDEBAR_GROUPS: AppShellSidebarNavGroup<AdminPanelSection>[] = [
  {
    title: ADMIN_PANEL_DATA_MANAGEMENT_GROUP_TITLE,
    dataTour: 'admin-panel-data-management',
    items: ADMIN_PANEL_DATA_MANAGEMENT_ITEMS,
  },
  {
    title: ADMIN_PANEL_REVENUE_GROUP_TITLE,
    dataTour: 'admin-panel-revenue',
    items: ADMIN_PANEL_REVENUE_ITEMS,
  },
  {
    title: ADMIN_PANEL_LOGS_GROUP_TITLE,
    dataTour: 'admin-panel-logs',
    items: ADMIN_PANEL_LOGS_ITEMS,
  },
];

export const ADMIN_PANEL_SIDEBAR_ITEMS: AppShellSidebarNavItem<AdminPanelSection>[] =
  ADMIN_PANEL_SIDEBAR_GROUPS.flatMap((group) => group.items);

export function getAdminPanelSectionTitle(section: AdminPanelSection): string {
  return ADMIN_PANEL_SIDEBAR_ITEMS.find((item) => item.id === section)?.title ?? '';
}

export function getAdminPanelSectionDescription(section: AdminPanelSection): string {
  return ADMIN_PANEL_SIDEBAR_ITEMS.find((item) => item.id === section)?.description ?? '';
}
